import {Component, Input, OnInit, Optional, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {ApiDataSource} from "../../../utils/api-data-source";
import {UtilsService} from "../../../shared/utils.service";
import {AppService} from "../../../app.service";
import {ApiRequestService} from "../../../shared/api-request.service";
import {Router} from "@angular/router";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {tap} from "rxjs/operators";
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {FolderLinkModel} from "../../../shared/folder-link.model";
import {FolderModel} from "../../../shared/folder.model";
import {UserPublicProfileComponent} from "../../../shared/user-public-profile/user-public-profile.component";
import { AccountDocsLinksViewComponent } from '../account-docs-links-view/account-docs-links-view.component';
import { AccountDocsLinksEditComponent } from '../account-docs-links-edit/account-docs-links-edit.component';
import { RBACService } from '../../../shared/rbac.service';

@Component({
    selector: 'app-account-docs-links',
    templateUrl: './account-docs-links.component.html',
    styleUrls: ['./account-docs-links.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AccountDocsLinksComponent implements OnInit {

  @Input() public folderId: number;
  @Input() public permissions: any;
  // The owning folder, used only to fall back to property inspection when the parent does not supply
  // a recognised permissions.feature. Passed as an input so this component never reaches into the
  // parent dialog's data.
  @Input() public folder: FolderModel;

  displayedColumns: string[] = [
    'select',
    'id',
    'url',
    'description',
    'created_by_user',
    'date_created',
    // 'date_created_UTC',
    'actions'
  ];

  dataSource = new AccountFoldersLinksDataSource(this.app, this.api);

  // the paginator and sorter
  @ViewChild('paginator1') paginator1: MatPaginator;
  @ViewChild('paginator2') paginator2: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  folderContext: string; // 'account', 'template', 'site', or 'employee'

  // RBAC permission flag. Resolved once in ngOnInit() (after the folder context is known) so the
  // template does not re-evaluate rbac.userCanAccess() on every change detection cycle.
  // Links are document metadata, so all link CRUD operations use the document EDIT permission.
  canEditLinks: boolean = false;

  constructor(
    public utils: UtilsService,
    public app: AppService,
    private api: ApiRequestService,
    public router: Router,
    private dialog: MatDialog,
    // This component is rendered as a tab child of AccountDocsLayoutComponent, which is always
    // opened via MatDialog, so the dialog ref resolves through the injector hierarchy. It is marked
    // optional so the component also works if it is ever embedded outside a dialog, in which case
    // there is simply no dialog to close.
    @Optional() private dialogRef: MatDialogRef<AccountDocsLinksComponent>,
    public rbac: RBACService
  ) { }

  // Expose RBACService to template
  protected readonly RBACService = RBACService;

  ngOnInit() {
    // Use the folder id supplied by the parent as an input.
    this.dataSource.folder_id = this.folderId;

    if(this.dataSource.folder_id) {
      // Determine context: prefer permissions.feature from parent, fallback to folder inspection
      this.folderContext = this.determineContextFromPermissions() || this.determineFolderContext();
      
      // Links are document metadata - all CRUD operations use document EDIT permission
      // Check if user has view OR edit permission for the document context
      const viewPermissions = this.getViewPermissionsForContext(this.folderContext);
      const editPermissions = this.getEditPermissionsForContext(this.folderContext);

      // Resolve the template permission flag once, now that the folder context is known.
      this.canEditLinks = this.rbac.userCanAccess(editPermissions, true);

      // Combine view and edit permissions and check if user has ANY of them
      const allPermissions = [...new Set([...viewPermissions, ...editPermissions])];
      
      if (!this.rbac.userCanAccess(allPermissions, false)) {
        this.rbac.showGenericAccessDeniedMessage(
          'You do not have permission to access document links.'
        );
        this.dialogRef?.close(true);
        return;
      }

      // Only load data if user has view permission
      if (this.rbac.userCanAccess(viewPermissions, true)) {
        this.dataSource.getData(true);
      }
    } else {
      this.dialogRef?.close(true);
    }
  }

  ngAfterViewInit() {
    // Reset the paginator when sorting takes place
    this.sort.sortChange.subscribe(() => {
      this.paginator1.pageIndex = 0;
      this.paginator2.pageIndex = 0;
    });

    const paginatorTap = tap((paginator) => {
      this.paginator1.pageIndex = paginator['pageIndex'];
      this.paginator1.pageSize = paginator['pageSize'];
      this.paginator2.pageIndex = paginator['pageIndex'];
      this.paginator2.pageSize = paginator['pageSize'];

      this.dataSource.limit = paginator['pageSize'];
      this.dataSource.offset = paginator['pageIndex'];
      this.dataSource.getData();
    });

    // Subscribe to the paginator tap events.
    this.paginator1.page.pipe(paginatorTap).subscribe();
    this.paginator2.page.pipe(paginatorTap).subscribe();

    // Subscribe to the sorter tap events.
    this.sort.sortChange.pipe(tap((sorter) => {
      this.dataSource.order_by = sorter['active'];
      this.dataSource.order = sorter['direction'];

      // Sort UTC columns based on their corresponding date fields.
      if ( sorter['active'] == 'date_created_UTC' ) {
        this.dataSource.order_by = 'date_created';
      }

      this.dataSource.getData();
    })).subscribe();
  }

  onView(id: number) {
    this.dialog
      .open(AccountDocsLinksViewComponent, {
        width: '1024px',
        data: {
          folder_link_id: id,
          folder_id: this.dataSource.folder_id,
          folderContext: this.folderContext
        }
      })
      .afterClosed()
      .subscribe((result) => {
        // Refresh the list regardless of how the dialog is closed.
        this.dataSource.getData(true);
      });
  }

  onAdd() {
    // Links are document metadata - creating links requires document EDIT permission
    const editPermissions = this.getEditPermissionsForContext(this.folderContext);
    
    if (!this.rbac.userCanAccess(editPermissions, true)) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to create document links.'
      );
      return;
    }

    this.openLinkDialog(new FolderLinkModel());
  }

  onEdit(folder_link: FolderLinkModel) {
    // Links are document metadata - editing links requires document EDIT permission
    const editPermissions = this.getEditPermissionsForContext(this.folderContext);
    
    if (!this.rbac.userCanAccess(editPermissions, true)) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to edit document links.'
      );
      return;
    }

    this.openLinkDialog(folder_link);
  }

  private openLinkDialog(folder_link: FolderLinkModel) {
    this.dialog
      .open(AccountDocsLinksEditComponent, {
        width: '1024px',
        data: {
          folder_link: folder_link,
          folder_id: this.dataSource.folder_id,
          folderContext: this.folderContext
        }
      })
      .afterClosed()
      .subscribe((result) => {
        // Refresh the list regardless of how the dialog is closed.
        this.dataSource.getData(true);
      });
  }

  onRemove(id: number) {
    this.remove([id]);
  }

  onRemoveSelected() {
    this.remove(this.dataSource.selection.selected);
  }

  private remove(ids: number[]) {
    // Links are document metadata - deleting links requires document EDIT permission
    const editPermissions = this.getEditPermissionsForContext(this.folderContext);
    
    if (!this.rbac.userCanAccess(editPermissions, true)) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete document links.'
      );
      return;
    }

    this.utils.showModal('Delete Document Link(s)', 'Are you sure you want to delete the selected Document Link(s)?', () => {
      this.api.makeRequest('delete', `v2/folders/${this.dataSource.folder_id}/links/${ids.join(',')}`)
        .then(() => {
          this.utils.showToast('The Document Link(s) were deleted.');
          this.dataSource.selection.clear();
        })
        .finally(() => {
          this.dataSource.getData(true);
        });
    });
  }

  onUserPublicView(hash: string) {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        // this.dataSource.getData(true);
      }
    );
  }

  /**
   * Determine folder context from the permissions input passed by the parent component.
   * This is more reliable than inspecting folder properties since the parent knows the context.
   */
  private determineContextFromPermissions(): string | null {
    if (!this.permissions?.feature) {
      return null;
    }
    
    const featureMap: { [key: string]: string } = {
      [RBACService.RBAC_F.F_ACCOUNT_COMPANY_DOCUMENTS]: 'account',
      [RBACService.RBAC_F.F_TEMPLATES_DOCUMENTS]: 'template',
      [RBACService.RBAC_F.F_SITES_DOCUMENTS]: 'site',
      [RBACService.RBAC_F.F_EMPLOYEES_TRAINING_COMPETENCIES]: 'employee',
      'user_profile': 'user_profile'
    };
    
    return featureMap[this.permissions.feature] || null;
  }

  /**
   * Determine folder context based on folder properties
   * This matches the backend context determination logic
   */
  private determineFolderContext(): string {
    const folder: any = this.folder;

    // Without a folder to inspect there is nothing to distinguish the perspectives, so fall back to
    // the account context, which is the most restrictive of the non-core sets.
    if (!folder) {
      return 'account';
    }

    if (folder.template) {
      return 'template';
    }
    if (folder.user_id) {
      return 'employee';
    }
    // Site documents are identified via associated sites (folder_site pivot table)
    if (folder.sites && folder.sites.length > 0) {
      return 'site';
    }
    return 'account';
  }

  /**
   * Get view permissions for the given context
   */
  private getViewPermissionsForContext(context: string): string[] {
    switch(context) {
      case 'account':
        return [
          RBACService.getPermission(
            RBACService.RBAC_F.F_ACCOUNT_COMPANY_DOCUMENTS,
            RBACService.RBAC_A.A_VIEW
          )
        ];
      case 'template':
        return [
          RBACService.getPermission(
            RBACService.RBAC_F.F_TEMPLATES_DOCUMENTS,
            RBACService.RBAC_A.A_VIEW
          )
        ];
      case 'site':
        return [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_DOCUMENTS,
            RBACService.RBAC_A.A_VIEW
          )
        ];
      case 'employee':
        return [
          RBACService.getPermission(
            RBACService.RBAC_F.F_EMPLOYEES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_EMPLOYEES_TRAINING_COMPETENCIES,
            RBACService.RBAC_A.A_VIEW
          )
        ];
      case 'user_profile':
        return [
          RBACService.RBAC_F.CORE_PERMISSION_USER_PROFILE
        ];
      default:
        return [];
    }
  }

  /**
   * Get edit permissions for the given context.
   * Links are document metadata - all link CRUD operations use document EDIT permission.
   */
  public getEditPermissionsForContext(context: string): string[] {
    switch(context) {
      case 'account':
        return [
          RBACService.getPermission(
            RBACService.RBAC_F.F_ACCOUNT_COMPANY_DOCUMENTS,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_ACCOUNT_COMPANY_DOCUMENTS,
            RBACService.RBAC_A.A_EDIT
          )
        ];
      case 'template':
        return [
          RBACService.getPermission(
            RBACService.RBAC_F.F_TEMPLATES_DOCUMENTS,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_TEMPLATES_DOCUMENTS,
            RBACService.RBAC_A.A_EDIT
          )
        ];
      case 'site':
        return [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_DOCUMENTS,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_DOCUMENTS,
            RBACService.RBAC_A.A_EDIT
          )
        ];
      case 'employee':
        return [
          RBACService.getPermission(
            RBACService.RBAC_F.F_EMPLOYEES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_EMPLOYEES_TRAINING_COMPETENCIES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_EMPLOYEES_TRAINING_COMPETENCIES,
            RBACService.RBAC_A.A_EDIT
          )
        ];
      case 'user_profile':
        return [
          RBACService.RBAC_F.CORE_PERMISSION_USER_PROFILE
        ];
      default:
        return [];
    }
  }

}

export class AccountFoldersLinksDataSource extends ApiDataSource {
  order_by = 'date_created';
  order = 'desc';
  searchBy = 'url';

  folder_id = 0;

  getData(resetOffset: boolean = false) {
    this.makeRequest(`v2/folders/${this.folder_id}/links`, resetOffset, {
      searchBy: this.searchBy
    });
  }
}
